import { FC, useEffect, useMemo, useState } from "react";
import billingDataRouteRequestsFactory from "./factory/billing-data-route-requests.factory";
import billingMapRoutesFactory from "./factory/billing-map-routes.factory";
import BillingsNode from "../../common/types/billings-node";
import billingRecalculateRequestFactory from "./factory/billing-recalculate-request.factory";
import billingDataFactory from "./factory/billing-data.factory";
import BillingDetailsComponent from "./billing-details/billing-details.component";
import ContractDetailsComponent from "./contract-details/contract-details.component";
import AdditionalOptionsComponent from "./additional-options/additional-options.component";
import billingSaveRequestFactory from "./factory/billing-save-request.factory";
import BonusType from "./types/bonus-type";
import PenaltyType from "./types/penalty-type";
import RelatedCargoRailyBillingsComponent from "./related-billings/cargo-raily/related-cargo-raily-billings.component";
import RelatedTaxiRailyBillingsComponent from "./related-billings/taxi-raily/related-taxi-raily-billings.component";
import ButtonComponent from "../../../../common/components/button/button.component";
import CardComponent from "../../../../common/components/card/card.component";
import Column from "../../../../common/components/grid/column";
import Row from "../../../../common/components/grid/row";
import MapComponent from "../../../../common/components/map/map.component";
import MapMarker from "../../../../common/components/map/types/map-marker";
import MapRoute from "../../../../common/components/map/types/map-route";
import SearchRoadRoutingResponse from "../../../../common/utils/search-road-route/search-road-routing.response";
import BillingFormData from "./types/billing-form.data";
import { useNavigate, useParams } from "react-router-dom";
import BillingRailyTaxiAddRouteParams from "../../common/routes/types/billing-raily-taxi-add-route-params";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import formValidationService from "../../../../common/utils/validation/form-validation.service";
import notificationService from "../../../../common/utils/notification/notification.service";
import billingRoutesHelper from "../../common/routes/billing-routes.helper";
import { useAppContext } from "../../../../context/app.context";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";
import BillingsSummaryConfirmationComponent from "./billings-summary-confirmation.component";
import billingRouteDetailsHelper from "../../common/route-details/common/billings-route-details.helper";
import BillingRouteDetailsFormDataValidationResult, {
  NodeValidationResult,
} from "../../common/route-details/types/billings-route-details-form-data-validation-result";
import billingRouteDetailsDataValidationService from "../../common/route-details/common/billing-route-details-form-data-validation.service";
import BillingsRouteDetailsFormComponent from "../../common/route-details/billings-route-details-form.component";
import MessengerComponent from "../../../../common/components/messenger/messenger.component";
import billingsCargoTaxiHelper from "./billings-raily-taxi.helper";
import billingsMapMarkersFactory from "../../common/map-markers/billings-map-markers.factory";
import useGpsCargoOrderRecords from "../../../../common/services/gps/cargo-order-records/use-gps-cargo-order-records";
import useAbort from "../../../../common/hooks/use-abort";
import GpsCargoOrderRecordsParams from "../../../../common/services/gps/cargo-order-records/gps-cargo-order-records-params";
import searchRoadRoutingService from "../../../../common/utils/search-road-route/search-road-routing.service";
import useBillingsOrderRailyTaxiDraft from "../../../../common/services/billings/order/raily-taxi/draft/use-billings-order-raily-taxi-draft";
import BillingsOrderRailyTaxiDraftParams from "../../../../common/services/billings/order/raily-taxi/draft/billings-order-raily-taxi-draft-params";
import useBillingsOrderRailyTaxiRecalculate from "../../../../common/services/billings/order/raily-taxi/recalculate/use-billings-order-raily-taxi-recalculate";
import billingService from "../../../../common/services/billings/billings.service";

type BillingsRailyTaxiAddProps = {};

const BillingsRailyTaxiAddComponent: FC<BillingsRailyTaxiAddProps> = () => {
  const { orderUuid } = useParams<BillingRailyTaxiAddRouteParams>();

  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const [isBillingSavePending, setIsBillingSavePending] = useState(false);

  const [orderInternalId, setOrderInternalId] = useState("");

  const [billingData, setBillingData] = useState<BillingFormData>();
  const [billingFormData, setBillingFormData] = useState<BillingFormData>();

  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>();
  const [plannedMapRoute, setPlannedMapRoute] = useState<MapRoute | null>(null);
  const [completedMapRoute, setCompletedMapRoute] = useState<MapRoute | null>(
    null,
  );

  const [shouldRetrieveRouteData, setShouldRetrieveRouteData] = useState(false);
  const [isSummaryConfirmationVisible, setIsSummaryConfirmationVisible] =
    useState(false);

  const gpsCargoOrderRecords = useGpsCargoOrderRecords();
  const gpsCargoOrderRecordsAbort = useAbort();

  const billingsRailyTaxiDraft = useBillingsOrderRailyTaxiDraft();
  const billingsRailyTaxiDraftAbort = useAbort();

  const billingsRailyTaxiRecalculate = useBillingsOrderRailyTaxiRecalculate();
  const billingsRailyTaxiRecalculateAbort = useAbort();

  const billingsRailyTaxiCreateAbort = useAbort();

  useEffect(() => {
    if (!orderUuid) return;

    const params: BillingsOrderRailyTaxiDraftParams = {
      orderUuid: orderUuid,
    };

    billingsRailyTaxiDraft.load(params, billingsRailyTaxiDraftAbort.signal);

    return billingsRailyTaxiDraftAbort.revoke;
  }, [orderUuid]);

  const [formValidationResults, setFormValidationResults] =
    useState<BillingRouteDetailsFormDataValidationResult>({
      nodeValidations: [],
      penaltyValue: formValidationService.defaultValidationResult,
      discountValue: formValidationService.defaultValidationResult,
    });

  useEffect(() => {
    if (!orderUuid) return;

    const params: GpsCargoOrderRecordsParams = {
      orderUuid: orderUuid,
    };

    gpsCargoOrderRecords.load(params, gpsCargoOrderRecordsAbort.signal);

    return gpsCargoOrderRecordsAbort.revoke;
  }, [orderUuid]);

  const navigate = useNavigate();

  const documentTitle = appTranslationsHelper
    .getDocumentTitleTranslations()
    .billingsTaxiWithRailyAdd.replace("#{orderInternalId}", orderInternalId);

  useDocumentTitle(documentTitle);

  useEffect(() => {
    const breadcrumbs = billingBreadcrumbsHelper.getRailyWithTaxiAddBreadcrumbs(
      {
        orderUuid: orderUuid!,
        orderInternalId,
      },
    );

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage, orderInternalId]);

  useEffect(() => {
    if (!billingData || !shouldRetrieveRouteData) {
      return;
    }

    const mapMarkers = billingsMapMarkersFactory.createMapMarkers(
      billingData.billingNodes,
    );

    const routeRequests =
      billingDataRouteRequestsFactory.createBillingDataRouteRequests(
        billingData.billingNodes,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    routeRequests.forEach((routeRequest) => {
      const fetch = searchRoadRoutingService.search(routeRequest);

      fetchPromises.push(fetch);
    });

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];

      responses.forEach((response, index) => {
        const mapRoute = response.routes[0]
          ? billingMapRoutesFactory.createMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }
      });

      const newMapRouteWaypoints: MapRoute["waypoints"] = [];

      mapRouteWaypointGroups.forEach((waypoint) => {
        newMapRouteWaypoints.push(...waypoint);
      });

      const newMapRoute: MapRoute = {
        waypoints: newMapRouteWaypoints,
        options: { color: "red" },
      };

      setPlannedMapRoute(newMapRoute);
      setMapMarkers(mapMarkers);
      setShouldRetrieveRouteData(false);
    });
  }, [shouldRetrieveRouteData]);

  const recalculateBilling = (formData: BillingFormData) => {
    if (!orderUuid) return;

    const params = billingRecalculateRequestFactory.createRecalculateRequest(
      orderUuid,
      formData,
    );

    billingsRailyTaxiRecalculate.load(
      params,
      billingsRailyTaxiRecalculateAbort.signal,
    );
  };

  const handleBillingCreateSuccess = () => {
    notificationService.success(translations.successNotificationText);
    navigateToListing();
  };

  const handleBillingCreateFailure = () => {
    notificationService.error(translations.failureNotificationText);
  };

  const navigateToListing = () => {
    navigate(billingRoutesHelper.getRailyUnbilledOrderListingRoute());
  };

  useEffect(() => {
    if (!billingsRailyTaxiDraft.data) return;

    const billingData = billingDataFactory.createBillingData(
      billingsRailyTaxiDraft.data,
    );

    setBillingData(billingData);
    setBillingFormData(billingData);
    setOrderInternalId(String(billingsRailyTaxiDraft.data.cargoOrder.humanId));
    setShouldRetrieveRouteData(true);

    setFormValidationResults((current) => ({
      discountValue: formValidationService.defaultValidationResult,
      nodeValidations: billingData.billingNodes.map((x) => {
        return {
          haltingTime: formValidationService.defaultValidationResult,
          distance: formValidationService.defaultValidationResult,
          highwayCharge: formValidationService.defaultValidationResult,
          position: x.position,
        };
      }),
      penaltyValue: current.penaltyValue,
    }));
  }, [billingsRailyTaxiDraft.data]);

  useEffect(() => {
    const gpsData: MapRoute = {
      waypoints: gpsCargoOrderRecords.data.map((x) => {
        return { latitude: x.lat, longitude: x.lon };
      }),
      options: { color: "blue" },
    };

    setCompletedMapRoute(gpsData);
  }, [gpsCargoOrderRecords.data]);

  const onBillingDataSave = () => {
    const isFormValid = areNodesValid() && validatePenalty();

    if (!isFormValid) return;

    if (orderUuid && billingFormData) {
      setIsBillingSavePending(true);

      const params = billingSaveRequestFactory.createSaveBillingRequest(
        orderUuid,
        billingFormData,
      );

      billingService
        .createOrderRailyTaxiBillings(
          params,
          billingsRailyTaxiCreateAbort.signal,
        )
        .then(handleBillingCreateSuccess)
        .catch(handleBillingCreateFailure)
        .finally(() => setIsBillingSavePending(false));
    }
  };

  const onPenaltyBlur = () => {
    const shouldRecalculate = validatePenalty() && areNodesValid();

    if (shouldRecalculate) {
      recalculateBilling(billingFormData!);
    }
  };

  const onBonusBlur = () => {
    const shouldRecalculate = validateBonus();

    if (shouldRecalculate) recalculateBilling(billingFormData!);
  };

  const validateBonus = () => {
    const validationResult =
      billingRouteDetailsDataValidationService.validateBonus(
        billingFormData?.bonus!,
      );

    setFormValidationResults((curr) => ({
      ...curr,
      discountValue: validationResult,
    }));

    return validationResult.isValid;
  };

  const validatePenalty = () => {
    const validationResult =
      billingRouteDetailsDataValidationService.validatePenalty(
        billingFormData?.penalty ?? 0,
      );

    setFormValidationResults((curr) => ({
      ...curr,
      penaltyValue: validationResult,
    }));

    return validationResult.isValid;
  };

  const onBonusChanged = (bonusValue: number) => {
    setBillingFormData((current) => ({ ...current!, bonus: bonusValue }));
  };

  const onBonusTypeChanged = (bonusType: BonusType) => {
    setBillingFormData((current) => ({
      ...current!,
      bonusType: bonusType,
    }));
  };

  const onPenaltyChanged = (penaltyValue: number) => {
    setBillingFormData((current) => ({ ...current!, penalty: penaltyValue }));
  };

  const onPenaltyTypeChanged = (penaltyType: PenaltyType) => {
    setBillingFormData((current) => ({
      ...current!,
      penaltyType: penaltyType,
    }));
  };

  const onBillingNodeChanged = (node: BillingsNode) => {
    const nodes = billingFormData?.billingNodes.filter(
      (x) => x.position !== node.position,
    )!;

    nodes.push(node);

    const sortedArray = nodes.sort((a, b) =>
      a.position < b.position ? -1 : 1,
    );

    setBillingFormData((current) => ({
      ...current!,
      billingNodes: sortedArray,
    }));

    setShouldRetrieveRouteData(true);
  };

  const onCopyFromPlannedDistance = () => {
    const newBillingNodes = billingFormData?.billingNodes.map((node) => {
      node.distance = node.plannedDistance;

      return node;
    });

    const newBillingFormData: BillingFormData = {
      ...billingFormData!,
      billingNodes: newBillingNodes!,
    };

    setBillingFormData(newBillingFormData);

    const result = billingRouteDetailsHelper.validateAllFieldsByType(
      formValidationResults,
      newBillingFormData.billingNodes,
      "distance",
    );

    nodeValidationResults(result.nodeResult!);

    if (result.isAllFormValid) recalculateBilling(newBillingFormData);
  };

  const areNodesValid = () => {
    return (
      billingFormData?.billingNodes
        .map((node) => {
          return billingRouteDetailsHelper.haltingTimeValidation(
            formValidationResults,
            node.haltingTime,
            node.position,
          );
        })
        .every((x) => x) ?? false
    );
  };

  const onInputBlur = () => {
    if (!areNodesValid()) return;

    recalculateBilling(billingFormData!);
  };

  useEffect(() => {
    if (!billingsRailyTaxiRecalculate.data) return;

    const billingData = billingDataFactory.createBillingData(
      billingsRailyTaxiRecalculate.data,
    );

    setBillingData(billingData);
  }, [billingsRailyTaxiRecalculate.data]);

  const onModalClose = () => {
    setIsSummaryConfirmationVisible(false);
  };

  const openModal = () => {
    setIsSummaryConfirmationVisible(true);
  };

  const mapRoutes: MapRoute[] = useMemo(() => {
    const finalMapRoutes: MapRoute[] = [];
    if (plannedMapRoute) {
      finalMapRoutes.push(plannedMapRoute);
    }
    if (completedMapRoute) {
      finalMapRoutes.push(completedMapRoute);
    }
    return finalMapRoutes;
  }, [plannedMapRoute, completedMapRoute]);

  const translations =
    billingsTranslationsHelper.getRailyTaxiAddBillingsTranslations();

  const validationOnBlur = (position: number, fieldName: string) => {
    type Node = keyof typeof node;

    const node = billingFormData?.billingNodes.find(
      (x) => x.position === position,
    );

    const fieldToValidate = node && node[fieldName as Node];

    const results = billingRouteDetailsHelper.validationOnBlur(
      fieldToValidate,
      formValidationResults,
      position,
      fieldName,
    );

    nodeValidationResults(results.nodeResult!);

    if (results.isAllFormValid) recalculateBilling(billingFormData!);
  };

  const nodeValidationResults = (node: NodeValidationResult[]) => {
    setFormValidationResults((curr) => ({
      ...curr,
      nodeValidations: node,
    }));
  };

  const messengerChannelsAvailability =
    billingsCargoTaxiHelper.getMessengerChannelAvailability();

  return (
    <>
      <div className="billings">
        <HeadingComponent
          title={translations.header.headingText.replace(
            "#{orderInternalId}",
            orderInternalId,
          )}
        />
        <Row>
          <Column lg={8}>
            <Row>
              <Column withPaddings>
                <div className="billings_map_wrapper">
                  <MapComponent
                    markers={mapMarkers}
                    autoFit
                    autoFitOnUpdate
                    routes={mapRoutes}
                  />
                </div>
              </Column>
              <Column withPaddings>
                <ContractDetailsComponent
                  isLoading={billingsRailyTaxiDraft.isLoading}
                  contractDetails={billingData?.contractDetails ?? null}
                />
              </Column>
              <Column withPaddings>
                <Row>
                  <Column lg={9}>
                    <BillingsRouteDetailsFormComponent
                      billingNodes={billingFormData?.billingNodes}
                      formValidationResults={
                        formValidationResults.nodeValidations
                      }
                      orderId={orderUuid!}
                      onBillingNodeChanged={onBillingNodeChanged}
                      onInputBlur={onInputBlur}
                      onCopyFromPlannedDistance={onCopyFromPlannedDistance}
                      validationOnBlur={validationOnBlur}
                    />
                  </Column>
                  <Column lg={3}>
                    <AdditionalOptionsComponent
                      billingData={billingFormData}
                      onBonusChanged={onBonusChanged}
                      onBonusTypeChanged={onBonusTypeChanged}
                      onPenaltyChanged={onPenaltyChanged}
                      onPenaltyTypeChanged={onPenaltyTypeChanged}
                      onPenaltyBlur={onPenaltyBlur}
                      onBonusBlur={onBonusBlur}
                      formValidationResults={formValidationResults}
                    />
                  </Column>
                </Row>
              </Column>
              <Column withPaddings>
                <CardComponent
                  classNames={{ root: "billing_details" }}
                  header={{ title: translations.billingSummary.summaryLabel }}
                >
                  <BillingDetailsComponent billingData={billingData} />
                </CardComponent>
              </Column>
              <Column>
                <ButtonComponent type="primary" onClick={openModal}>
                  {translations.submitLabel}
                </ButtonComponent>
              </Column>
              <Column withPaddings>
                <RelatedCargoRailyBillingsComponent orderId={orderUuid!} />
              </Column>
              <Column withPaddings>
                <RelatedTaxiRailyBillingsComponent orderId={orderUuid!} />
              </Column>
            </Row>
          </Column>
          <Column lg={4} withPaddings>
            {!!billingData && (
              <CardComponent classNames={{ root: "h-50", content: "h-100" }}>
                <MessengerComponent
                  channelsAvailability={messengerChannelsAvailability}
                  orderUuid={orderUuid}
                  planEntryUuid={billingData.planEntryId}
                />
              </CardComponent>
            )}
          </Column>
        </Row>
        <BillingsSummaryConfirmationComponent
          isVisible={isSummaryConfirmationVisible}
          onSubmit={onBillingDataSave}
          onClose={onModalClose}
          billingData={billingData!}
          isLoading={isBillingSavePending}
        />
      </div>
    </>
  );
};

export default BillingsRailyTaxiAddComponent;
